import {
  DownArrowIcon,
  MorningIcon,
  MotionActivityIcon,
  MotionEmpty,
  NightIcon,
  RightArrowIcon,
  UpArrowIcon,
} from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import {
  accumulateDataPerHour,
  accumulateDataPerQuarter,
  convertTo24HourFormat,
  dayORNight,
  ensureLastHourInData,
  ensureLastQuarterInData,
  filterBySelectedDate,
  get_utc_offset,
  getAllDevicesDataPerHour,
  getAllDevicesDataPerQuarter,
  separateDeviceData,
  sortEventsByStartTime,
  timeArray,
  updateEventsData,
} from '@/Components/MotionActivity/MotionActivityUtils';
import { ACCESSORIES_PROPERTIES } from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getUserConfig } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { DefaultTimezone } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { Card, Tooltip } from 'antd';
import { get, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import MOTION_RANGES from '../../../../../../motionRanges.json';
import '../RecentActivity.less';
import {
  formatTime,
  getDateLabel,
  LoadingCard,
  reduceAccessoryToMotionDevice,
} from '../RecentActivityUtils';

const MotionActivityModal = ({
  useruuid,
  timezone,
  is24HourFormat,
  locale,
  data,
  accessories,
  latestMotionData,
}) => {
  const trans = useSelector(translator);
  const currentUserConfig = useSelector(getUserConfig(useruuid));
  const tz = timezone || DefaultTimezone;
  const timezoneOffset = useMemo(() => get_utc_offset(timezone, DefaultTimezone), [timezone]);
  const yesterday = useMemo(() => moment().tz(tz).subtract(1, 'day'), [tz]);
  const deviceuuid = get(latestMotionData, 'deviceuuid', '');

  const rangeConfig = useMemo(() => {
    const cfg = currentUserConfig?.portal_motion_bubble;
    const {
      SMALL_RANGE_MIN = 0,
      SMALL_RANGE_MAX = 50,
      SMALL_RANGE_VALUE = 8,
      MEDIUM_RANGE_MIN = 50,
      MEDIUM_RANGE_MAX = 100,
      MEDIUM_RANGE_VALUE = 13,
      LARGE_RANGE_MIN = 100,
      LARGE_RANGE_MAX = 200,
      LARGE_RANGE_VALUE = 18,
      XLARGE_RANGE_MIN = 200,
      XLARGE_RANGE_VALUE = 28,
    } = MOTION_RANGES || {};
    return {
      smallRangeMin: Number(cfg?.small?.min ?? SMALL_RANGE_MIN),
      smallRangeMax: Number(cfg?.small?.max ?? SMALL_RANGE_MAX),
      smallRangeVal: Number(cfg?.small?.circle_radius ?? SMALL_RANGE_VALUE),
      mediumRangeMin: Number(cfg?.medium?.min ?? MEDIUM_RANGE_MIN),
      mediumRangeMax: Number(cfg?.medium?.max ?? MEDIUM_RANGE_MAX),
      mediumRangeVal: Number(cfg?.medium?.circle_radius ?? MEDIUM_RANGE_VALUE),
      largeRangeMin: Number(cfg?.large?.min ?? LARGE_RANGE_MIN),
      largeRangeMax: Number(cfg?.large?.max ?? LARGE_RANGE_MAX),
      largeRangeVal: Number(cfg?.large?.circle_radius ?? LARGE_RANGE_VALUE),
      xLargeRangeMin: Number(cfg?.xlarge?.min ?? XLARGE_RANGE_MIN),
      xLargeRangeVal: Number(cfg?.xlarge?.circle_radius ?? XLARGE_RANGE_VALUE),
    };
  }, [currentUserConfig]);

  const {
    smallRangeMin,
    smallRangeMax,
    smallRangeVal,
    mediumRangeMin,
    mediumRangeMax,
    mediumRangeVal,
    largeRangeMin,
    largeRangeMax,
    largeRangeVal,
    xLargeRangeMin,
    xLargeRangeVal,
  } = rangeConfig;

  const motionBubble = (occ) => {
    if (occ === 0) return 0;
    if (occ > smallRangeMin && occ < smallRangeMax) return smallRangeVal;
    if (occ >= mediumRangeMin && occ < mediumRangeMax) return mediumRangeVal;
    if (occ >= largeRangeMin && occ < largeRangeMax) return largeRangeVal;
    if (occ >= xLargeRangeMin) return xLargeRangeVal;
  };

  const motionDevice = useMemo(() => {
    const accessory = (accessories || []).find(
      (a) => get(a, ACCESSORIES_PROPERTIES.DEVICE_UUID) === deviceuuid
    );
    return isEmpty(accessory) ? {} : { [deviceuuid]: reduceAccessoryToMotionDevice(accessory) };
  }, [accessories, deviceuuid]);

  const motionDeviceLocation = useMemo(
    () =>
      get(motionDevice, [deviceuuid, ACCESSORIES_PROPERTIES.LOCATION], '') ||
      get(motionDevice, [deviceuuid, ACCESSORIES_PROPERTIES.MODEL_NUMBER], ''),
    [deviceuuid, motionDevice]
  );

  const filteredDeviceData = useMemo(() => {
    if (!deviceuuid) return data || [];

    return (data || []).filter((event) => event?.deviceuuid === deviceuuid);
  }, [data, deviceuuid]);

  const { dataPerHour, dataPerQuarter } = useMemo(() => {
    if (isEmpty(filteredDeviceData) || isEmpty(motionDevice)) {
      return { dataPerHour: {}, dataPerQuarter: {} };
    }
    try {
      const loginUserTimeformat = { is24hoursformat: is24HourFormat };
      const adjusted = updateEventsData(filteredDeviceData, timezoneOffset, 'motion');
      const filtered = filterBySelectedDate(adjusted, yesterday.format('YYYY-MM-DD'));
      const sorted = sortEventsByStartTime(filtered);
      const byDevice = separateDeviceData(sorted);
      const perHour = Object.keys(byDevice).length
        ? accumulateDataPerHour(byDevice, motionDevice, tz, loginUserTimeformat)
        : {};
      const perQuarter = Object.keys(byDevice).length
        ? accumulateDataPerQuarter(byDevice, motionDevice, tz, loginUserTimeformat)
        : {};
      const updatedHour = ensureLastHourInData(perHour);
      const updatedQuarter = ensureLastQuarterInData(perQuarter);
      return {
        dataPerHour: getAllDevicesDataPerHour(updatedHour, motionDevice) || {},
        dataPerQuarter: getAllDevicesDataPerQuarter(updatedQuarter, motionDevice) || {},
      };
    } catch (e) {
      console.error('MotionActivityModal: error processing motion data', e);
      return { dataPerHour: {}, dataPerQuarter: {} };
    }
  }, [is24HourFormat, filteredDeviceData, motionDevice, timezoneOffset, yesterday, tz]);

  const lastHourEntry = useMemo(() => {
    if (isEmpty(dataPerHour)) return -1;

    const [{ hourlyData = {} } = {}] = Object.values(dataPerHour);
    const keys = Object.keys(hourlyData?.hourlyData || {});

    if (!keys.length) return parseInt(yesterday.format('H'), 10);

    const lastKey = parseInt(keys[keys.length - 1], 10);
    return isNaN(lastKey) ? -1 : lastKey;
  }, [dataPerHour, yesterday]);

  if (isEmpty(dataPerHour)) {
    return <div className="recent-calls-empty">{trans('NODATAAVAIALBLE')}</div>;
  }

  const renderMotionAsPercent = (deviceModel, hoursIn24Format, hourlyData, quarterlyData) => {
    if (hourlyData == null) return null;
    let visible = '';
    if (!Object.prototype.hasOwnProperty.call(hourlyData, hoursIn24Format)) {
      visible = 'hidden';
    }

    const commonStyles = {
      border: '0px',
      borderRadius:
        hoursIn24Format === '0'
          ? '5px 0px 0px 5px'
          : hoursIn24Format === '23'
            ? '0px 5px 5px 0px'
            : '',
    };

    const qBucket = quarterlyData?.[Number(hoursIn24Format)] || {};
    const qKeys = ['q0', 'q1', 'q2', 'q3'];
    const qVals = qKeys.map((k) => Number(qBucket?.[k] ?? 0));
    const widthFromCount = (val) => `${Math.max(2, Math.round((motionBubble(val) || 0) / 2))}px`;

    const colorFor = (val) => {
      if (val <= 2) return 'transparent';
      if (val <= 50) return '#FFD6A5';
      if (val <= 100) return '#FFB347';
      if (val <= 150) return '#FF8C00';
      return '#E65100';
    };

    return visible === 'hidden' ? (
      <div className="ram-motion-empty-block" style={commonStyles}>
        <MotionEmpty style={{ margin: '6px' }} />
      </div>
    ) : (
      <div
        className="ram-motion-percent-block"
        style={{
          ...commonStyles,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '40px',
        }}
      >
        {qKeys.map((q, i) => {
          const v = qVals[i];
          return (
            <div
              key={q}
              className={`ram-quarter-line ${q} ${v > 0 ? 'active' : ''}`}
              style={{
                width: widthFromCount(v),
                background: v > 0 ? colorFor(v) : 'transparent',
                transition: 'width 0.2s ease',
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderMorningNightIcon = (hour) => {
    const icon = hour === '6 AM' ? <MorningIcon /> : hour === '6 PM' ? <NightIcon /> : null;
    return <div style={{ width: '12px', height: '16px' }}>{icon}</div>;
  };

  const renderHours = (
    deviceModel,
    hour,
    hourlyData,
    timeRanges,
    quarterlyData,
    activityRangesHour
  ) => {
    const hoursIn24Format = convertTo24HourFormat(hour);
    const isLaterHour = parseInt(hoursIn24Format) > lastHourEntry;
    const hasZeroData =
      Object.prototype.hasOwnProperty.call(hourlyData, hoursIn24Format) &&
      hourlyData[hoursIn24Format] === 0;

    const timeOfDay =
      hoursIn24Format >= 6 && hoursIn24Format < 12
        ? trans('MORNING')
        : hoursIn24Format >= 12 && hoursIn24Format < 18
          ? trans('AFTERNOON')
          : hoursIn24Format >= 18 && hoursIn24Format < 24
            ? trans('EVENING')
            : trans('NIGHT');
    const timeRange =
      timeRanges && timeRanges[hoursIn24Format]
        ? `${timeRanges[hoursIn24Format].firstReportedTime} - ${timeRanges[hoursIn24Format].lastReportedTime}`
        : 'N/A';
    const event_value = Object.prototype.hasOwnProperty.call(hourlyData, hoursIn24Format)
      ? hourlyData[hoursIn24Format]
      : '--';
    const hourKey = Number(hoursIn24Format);
    const firstMotion = activityRangesHour?.[hourKey]?.firstMotionTime ?? '—';
    const lastMotion = activityRangesHour?.[hourKey]?.lastMotionTime ?? '—';

    const activity_value =
      event_value === '--'
        ? '--'
        : event_value === 0
          ? trans('NONE')
          : event_value > smallRangeMin && event_value < smallRangeMax
            ? trans('LOW')
            : event_value >= mediumRangeMin && event_value < mediumRangeMax
              ? trans('MEDIUM')
              : event_value >= largeRangeMin && event_value < largeRangeMax
                ? trans('HIGH')
                : trans('VERYHIGH');

    const tooltipContent = (
      <div className="tooltip-content">
        <div className="tooltip-info">
          <div className="tooltip-label">
            <div>{timeOfDay}</div>
            <div>{timeRange}</div>
          </div>
          <div className="tooltip-activity">
            <div>
              {trans('ACTIVITY:')} {activity_value}
            </div>
            <div>
              {trans('EVENTS:')} {event_value}
            </div>
            <div>
              {trans('FIRST_MOTION')}: {firstMotion}
            </div>
            <div>
              {trans('LAST_MOTION')}: {lastMotion}
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div key={hour}>
        <div className="ram-main-hour-container">
          <div className="ram-day-night-theme">{renderMorningNightIcon(hour)}</div>
          <div className="ram-day-night-time">
            {is24HourFormat
              ? convertTo24HourFormat(hour, true)
              : (() => {
                  if (hour === '12 AM' || hour === '6 AM' || hour === '12 PM' || hour === '6 PM') {
                    return hour;
                  }
                  return hour.replace(/AM|PM/, '');
                })()}
          </div>
          {!isLaterHour && (
            <Tooltip title={tooltipContent} placement="bottom" overlayClassName="custom-tooltip">
              <div
                className={`${dayORNight(hour) === 'night' ? 'ram-night-time-block' : 'ram-day-time-block'} ${hour === '12 AM' ? 'start' : hour === '11 PM' ? 'end' : ''}`}
              >
                <div className="ram-motion-activity-block">
                  {!hasZeroData
                    ? renderMotionAsPercent(deviceModel, hoursIn24Format, hourlyData, quarterlyData)
                    : null}
                </div>
              </div>
            </Tooltip>
          )}
          {isLaterHour && (
            <div
              className={`ram-future-hour-block ${hour === '12 AM' ? 'start' : hour === '11 PM' ? 'end' : ''}`}
            />
          )}
        </div>
      </div>
    );
  };

  const renderMotionActivity = (deviceData) => {
    return (
      <div className="ram-recent-motion-hours-row">
        <div className="ram-date-label-container">
          {getDateLabel(yesterday.toISOString(), trans, tz, locale)}
        </div>
        {timeArray.map((hour) =>
          renderHours(
            deviceData.model,
            hour,
            deviceData.hourlyData?.hourlyData,
            deviceData.hourlyData?.timeRanges,
            deviceData?.quarterlyData?.quarterlyData,
            deviceData?.quarterlyData?.activityRangesHour
          )
        )}
      </div>
    );
  };

  return (
    <>
      {Object.keys(dataPerHour).map((deviceKey) => {
        const item = {
          ...dataPerHour[deviceKey],
          quarterlyData: dataPerQuarter?.[deviceKey]?.quarterlyData,
        };
        return (
          <div className="ram-main-activity-container" key={deviceKey}>
            <div className="ram-motion-activity-header">
              <span className="ram-motion-activity-device-location">{motionDeviceLocation}</span>
            </div>
            <div className="ram-motion-activity-container">{renderMotionActivity(item)}</div>
          </div>
        );
      })}
    </>
  );
};

export const MotionCard = (props) => {
  const { timezone, locale, is24HourFormat, data, isLoading, trendData } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  const latestMotionData = useMemo(() => {
    if (isEmpty(data)) return {};

    const sortedMotionData = [...data].sort(
      (a, b) => new Date(b.utc_timestamp_inms) - new Date(a.utc_timestamp_inms)
    );
    return sortedMotionData[0];
  }, [data]);

  if (isLoading) return <LoadingCard />;

  const modalConfig = {
    title: (
      <div className="recent-activity-modal-title">
        <MotionActivityIcon className="recent-activity-modal-title-icon" />
        <span>{trans('RECENT_MOTION')}</span>
      </div>
    ),
    content: <MotionActivityModal {...props} latestMotionData={latestMotionData} />,
  };

  const location = get(latestMotionData, 'location', '');
  const timestamp = get(latestMotionData, 'utc_timestamp_inms', '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);

  const trendResults = get(trendData, 'trendResults', {});
  const allTrendPercents = Object.values(trendResults)
    .map((byDevice) => get(byDevice, ['motion_detected', 'trendPercent'], null))
    .filter((v) => v !== null);
  const trendPercent = allTrendPercents.length
    ? Math.round(allTrendPercents.reduce((a, b) => a + b, 0) / allTrendPercents.length)
    : null;
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const handleCardClick = (e) => {
    e.preventDefault();
    openDashboardModal(modalConfig);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDashboardModal(modalConfig);
    }
  };

  const CardTitle = (
    <div className="recent-activity-card-header">
      <MotionActivityIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('MOTION')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      variant="borderless"
      hoverable
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        {location && <p className="recent-activity-context">{location}</p>}
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('ALL_LOCATIONS')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

MotionActivityModal.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  latestMotionData: PropTypes.object,
};

MotionCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};
